import { aiRouter } from '@apps/ai';
import { faRouter } from '@apps/fa';
import { systemManagementRouter } from '@apps/system-management';
import { lazyPage, NAV_STALL_EVENT, type NavStallDetail } from '@libs/templates/routing';
import { Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router';
import { AppContainer, RootContainer, ShellContainer } from './layouts';
import { AdminLogin } from './pages/AdminLogin';
import { AdminMfaVerify } from './pages/AdminMfaVerify';
import { AuthCallbackPage } from './pages/AuthCallBack';
import { AuthErrorView } from './pages/AuthErrorView';
import { ErrorBoundary } from './pages/ErrorBoundary';
import { Login } from './pages/Login';
import { SandboxLogin } from './pages/SandboxLogin';
import { TopPage } from './pages/TopPage';
import {
  findLastApiBefore,
  type LongTask,
  recordLongTask,
  shouldReportLongTask,
  sumLongTaskSince,
} from './services/longTaskMonitor';
import {
  buildJsErrorFields,
  buildLongTaskFields,
  buildNavStallFields,
  buildReactErrorFields,
  buildRejectionFields,
  buildResourceErrorFields,
  collectNavStallEnv,
  getPageContext,
  type PageContext,
} from './services/pendoErrorFields';
import { type PendoField, sanitizeForPendo } from './services/pendoSanitizer';
import { userRouter } from './userRouter';

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootContainer />}>
      <Route element={<ShellContainer />}>
        <Route index element={<TopPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin-login/mfa-verify" element={<AdminMfaVerify />} />
        <Route path="/sandbox-login" element={<SandboxLogin />} />
        <Route
          path="/user/change-password/first-time"
          lazy={lazyPage(async () => import('./pages/FirstTimeChangePassword'))}
        />
      </Route>
      <Route path="/callback" element={<AuthCallbackPage />} />
      <Route path="/error" element={<AuthErrorView />} />
      <Route element={<AppContainer />} errorElement={<ErrorBoundary />}>
        {userRouter}
        {systemManagementRouter}
        {faRouter}
        {aiRouter}
      </Route>
      <Route path="*" lazy={lazyPage(async () => import('@libs/templates/template-pages/NotFound'))} />
    </Route>,
  ),
);

const ACCESS_RESTRICTED_ERROR_MESSAGE = '管理者によりアクセスが制限されています。';

/**
 * ppfw-clientのTokenRefreshRequiredErrorを除外する
 */
const isTokenRefreshError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as {
    constructor?: { name?: string };
  };

  return err.constructor?.name === 'TokenRefreshRequiredError';
};

/**
 * アクセス制限エラーをPendo送信対象から除外する
 */
const isAccessRestrictedError = (error: unknown): boolean => {
  return error instanceof Error && error.message === ACCESS_RESTRICTED_ERROR_MESSAGE;
};

const pendoTrackErrorEvent = (event: string, buildFields: (ctx: PageContext) => PendoField[]) => {
  if (window.pendo) {
    const payload = sanitizeForPendo(event, buildFields(getPageContext()));

    window.pendo.track('frontend_error', payload);
  }
};

function Shell() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [hasScriptError, setHasScriptError] = useState(false);

  useEffect(() => {
    // catching resource loading errors and global JS errors
    const handleErrorEvent = (event: ErrorEvent) => {
      const target = event.target;

      if (target instanceof HTMLElement) {
        // resource loading error
        const src = 'src' in target ? (target.src as string) : '';
        const href = 'href' in target ? (target.href as string) : '';

        pendoTrackErrorEvent('resource_error', (ctx) =>
          buildResourceErrorFields({ tagName: target.tagName, src: src || href }, ctx),
        );
      } else {
        // js error
        // ResizeObserverの警告は無害のため無視する
        if (event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
          return;
        }

        pendoTrackErrorEvent('js_error', (ctx) => buildJsErrorFields(event, ctx));
      }
    };
    window.addEventListener('error', handleErrorEvent, true);

    // catching unhandled promise exceptions
    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      // Token refresh の 401 エラーは正常フローなので除外
      if (isTokenRefreshError(event.reason)) {
        return;
      }
      // アクセス制限エラーはPendoへの送信対象外
      if (isAccessRestrictedError(event.reason)) {
        return;
      }

      pendoTrackErrorEvent('unhandled_promise_rejection', (ctx) => buildRejectionFields(event.reason, ctx));
    };
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    // errorBoundary routeError
    const handleReactError = (event: Event) => {
      const customEvent = event as CustomEvent;

      // Token refresh の 401 エラーは正常フローなので除外
      if (isTokenRefreshError(customEvent.detail)) {
        return;
      }
      // アクセス制限エラーはPendoへの送信対象外
      if (isAccessRestrictedError(customEvent.detail)) {
        return;
      }

      pendoTrackErrorEvent('react_error', (ctx) => buildReactErrorFields(customEvent.detail ?? {}, ctx));
    };
    window.addEventListener('react-error', handleReactError);

    // main thread の長い停止（クリックが処理されない）を記録・通知する
    const longTasks: LongTask[] = [];
    let lastLongTaskReportedAt: number | undefined;
    const isLongTaskSupported =
      typeof PerformanceObserver !== 'undefined' &&
      PerformanceObserver.supportedEntryTypes?.includes('longtask');
    const longTaskObserver = isLongTaskSupported
      ? new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const now = performance.now();
            recordLongTask(longTasks, entry, now);
            if (shouldReportLongTask(entry.duration, lastLongTaskReportedAt, now)) {
              lastLongTaskReportedAt = now;
              const after = findLastApiBefore(performance.getEntriesByType('resource'), entry.startTime);
              pendoTrackErrorEvent('long_task', (ctx) => buildLongTaskFields(entry.duration, after, ctx));
            }
          }
        })
      : undefined;
    longTaskObserver?.observe({ type: 'longtask', buffered: true });

    // lazyPage が通知する、画面遷移の読み込みが終わらない状態
    const handleNavStall = (event: Event) => {
      const { detail } = event as CustomEvent<NavStallDetail>;
      const env = collectNavStallEnv(sumLongTaskSince(longTasks, performance.now() - detail.waitedMs));
      pendoTrackErrorEvent('nav_stall', (ctx) => buildNavStallFields(detail, env, ctx));
    };
    window.addEventListener(NAV_STALL_EVENT, handleNavStall);

    if (!process.env.PENDO_API_KEY) {
      setIsScriptLoaded(true);
    } else {
      const src = `https://cdn.pendo.io/agent/static/${process.env.PENDO_API_KEY}/pendo.js`;
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        setIsScriptLoaded(true);
      } else {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => setIsScriptLoaded(true);
        script.onerror = () => setHasScriptError(true);
        document.head.appendChild(script);
      }
    }

    return () => {
      window.removeEventListener('error', handleErrorEvent, true);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
      window.removeEventListener('react-error', handleReactError);
      window.removeEventListener(NAV_STALL_EVENT, handleNavStall);
      longTaskObserver?.disconnect();
    };
  }, []);

  if (hasScriptError) {
    return (
      <div>Failed to load external script, please refresh the page or contact system administrator.</div>
    );
  }
  if (!isScriptLoaded) {
    return null;
  }

  return (
    <Suspense>
      <RouterProvider router={router} />
    </Suspense>
  );
}

export { Shell };
